import {
  InitiateWorkerWithdrawalRequest,
  WorkerWithdrawalPreviewResponse,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bank';
import {
  IAddress,
  IAddressEntity,
  ICompany,
  IContractRenewalSettings,
  IEmployeeWithContractDetails,
  IW2PayrollInfo,
  IW2PaySchedule,
} from 'types';
import { callApi } from '../config';

export type StateRegistrationRequest = {
  state: string;
  numberOfEmployees: number;
  employeeStartDates: string[];
};

export const requestStateRegistration = async (companyId: string, data: StateRegistrationRequest): Promise<void> => {
  await callApi(`/companies/${companyId}/state-registration-request`, 'post', data);
};

export const createCompany = async (data) => {
  return await callApi(`/company`, 'post', data);
};

export const createW2Company = async (data, companyId) => {
  return await callApi(`/companies/${companyId}/w2`, 'post', data);
};

export const getCompany = async (data) => {
  return await callApi(`/company`, 'get', data);
};

/** deprecated */
export const getEmployerCompanyNames = async () => {
  return await callApi(`/employer-company-names`, 'get', {});
};

export const getCompanyAdmins = async (data) => {
  return await callApi(`/companies/admins`, 'post', data);
};

export const updateEmployeeAddress = async (
  employeeID: string,
  data: {
    userType?: 'person' | 'entity';
    address?: IAddress;
    usTaxpayer?: boolean;
    nonUsTaxpayer?: boolean;
    entityInformation?: IAddressEntity;
  },
) => {
  return await callApi(`/companies/employees/${employeeID}`, 'patch', data);
};

export const updateNotificationsAlert = async (
  employeeID: string,
  data: {
    notificationsByCompanyId?: {
      [key: string]: {
        notifyAdminsOnInvoiceSubmission?: boolean;
        notifyAdminsOnPTOSubmission?: boolean;
      };
    };
  },
) => {
  return await callApi(`/companies/admins/${employeeID}`, 'patch', data);
};

export const updateCompanyDetails = async (companyId: string, data: Partial<ICompany> & { live?: boolean }) => {
  return await callApi(`/company/${companyId}`, 'patch', data);
};

export const updateCompanyAddress = async (companyId: string, data: { address: IAddress }) => {
  return await callApi(`/company/${companyId}`, 'patch', data);
};

export const updateCompanySettings = async (
  companyId: string,
  data: {
    settings: {
      active?: boolean;
      isTpOnboardingSkipped?: boolean;
      autoApproveTimeOffRequests?: boolean;
      autoApproveFixedRateInvoices?: boolean;
      blockContractorInvoiceSubmission?: boolean;
      isKYCMandated?: boolean;
      autoPayAheadOfHolidaysWeekends?: boolean;
      paymentAllowedDaysBeforeDueDate?: number;
      /** Fields left out keep their current value. */
      contractRenewals?: Partial<IContractRenewalSettings>;
      defaultVerificationSetting?: {
        kycEnabled: boolean;
        complianceEnabled: boolean;
      };
      enabledApps?: {
        payroll?: boolean;
        ap?: boolean;
        ar?: boolean;
        banking?: boolean;
      };
      requestedToEnableApps?: {
        payroll?: boolean;
        ap?: boolean;
        ar?: boolean;
        banking?: boolean;
      };
    };
  },
) => {
  return await callApi(`/company/${companyId}`, 'patch', data);
};

export const getEmployeeDetails = async (employeeID: string, companyID: string) => {
  const response: Promise<IEmployeeWithContractDetails> = await callApi(
    `/companies/${companyID}/employees/${employeeID}`,
    'get',
    {},
  );
  return response;
};

export const getCompanyEmployees = async (companyID: string) => {
  return await callApi(`/employees`, 'get', {
    companyID,
  });
};

export const sendInviteEmail = (employeeID: string) => {
  return callApi(`/companies/employees/${employeeID}/invite`, 'post', {});
};

export interface IEmployeeFirstPay {
  contractId: string;
  firstPayDate?: string | null;
  firstPayPeriodStartDate?: string | null;
  firstPayAmountInCents?: number | null;
}

export const getEmployeeFirstPayConfigurations = async (companyId: string): Promise<IEmployeeFirstPay[]> =>
  await callApi(`/companies/${companyId}/w2-payroll/first-pay`, 'get', {});

export const updateEmployeeFirstPay = async (
  companyId: string,
  contractId: string,
  body: {
    firstPayDate?: string | null;
    firstPayPeriodStartDate?: string | null;
    firstPayAmountInCents?: number | null;
  },
): Promise<IEmployeeFirstPay> =>
  await callApi(`/companies/${companyId}/w2-payroll/contracts/${contractId}/first-pay`, 'put', body);

export const addW2Employee = async ({
  companyID,
  checkEmployeeId,
  firstName,
  lastName,
  email,
  checkCompanyId,
  teamId,
  checkPayScheduleId,
  contractDocumentType,
  customContractTemplateId,
  companySigningAuthorityUserId,
  jobTitle,
  startDate,
  scopeOfWork,
  wageType,
  rate,
  contractCurrency,
  customFields,
  requiresI9,
  firstPayDate,
  firstPayPeriodStartDate,
  firstPayAmountInCents,
}: {
  companyID: string;
  checkEmployeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  checkCompanyId: string;
  teamId: string;
  checkPayScheduleId?: string;
  contractDocumentType?: 'CONTRACT_WIZARD' | 'CUSTOM_CONTRACT' | null;
  customContractTemplateId?: string | null;
  companySigningAuthorityUserId?: string | null;
  jobTitle?: string | null;
  startDate?: string | null;
  scopeOfWork?: string | null;
  wageType?: string | null;
  rate?: number | null;
  contractCurrency?: string | null;
  customFields?: Record<string, string> | null;
  requiresI9?: boolean | null;
  firstPayDate?: string | null;
  firstPayPeriodStartDate?: string | null;
  firstPayAmountInCents?: number | null;
}) => {
  const data = {
    companyID,
    checkEmployeeId,
    checkCompanyId,
    firstName,
    lastName,
    email,
    teamId,
    checkPayScheduleId,
    contractDocumentType,
    customContractTemplateId,
    companySigningAuthorityUserId,
    jobTitle,
    startDate,
    scopeOfWork,
    wageType,
    rate,
    contractCurrency,
    customFields,
    requiresI9,
    firstPayDate,
    firstPayPeriodStartDate,
    firstPayAmountInCents,
  };
  return await callApi(`/companies/employees/w2`, 'post', data);
};

export const updateRecurrences = async (companyId: string, data: any) => {
  return await callApi(`/companies/${companyId}/recurrences`, 'post', data);
};

export const getCompanyW2PayrollInfo = async (companyID: string, startDate?: string) => {
  let url = `/companies/${companyID}/w2-payroll/info`;
  if (startDate) {
    url += `?startDate=${startDate}`;
  }
  const response: Promise<IW2PayrollInfo[]> = await callApi(url, 'get', {});
  return response;
};

export const getNextCheckDate = async (companyID: string, startDate: string) => {
  return await callApi(`/companies/${companyID}/w2-payroll/info?startDate=${startDate}`, 'get', {});
};

export type PendingSignatureContract = {
  contractId: string;
  contractDocumentId: string;
  userId: string;
  name: string;
  employmentType: string;
  companySigned: boolean;
  contractorSigned: boolean;
  signingAuthorityUserId?: string;
  signingAuthorityName?: string;
  sharedLink?: string;
  createdAt?: string;
  daysOutstanding?: number;
};

export type PendingSignaturesResponse = {
  companyId: string;
  totalCount: number;
  contracts: PendingSignatureContract[];
};

export const getPendingSignatureContracts = async (companyID: string): Promise<PendingSignaturesResponse> => {
  return await callApi(`/companies/${companyID}/contracts/pending-signatures`, 'get', {});
};

export const updateAutopilotStatus = (companyID: string, autopilotOn: boolean) => {
  return callApi(`/companies/${companyID}/w2-payroll/autopilot?autopilotOn=${autopilotOn}`, 'post', {});
};

export const updateCompanyW2Payroll = async (
  companyId: string,
  data: { w2Payroll: { firstCheckDate?: string; payFrequency: IW2PaySchedule; autopilotOn: boolean } },
) => {
  return await callApi(`/company/${companyId}`, 'patch', data);
};

/**
 * Updates the W2 payroll onboarding configuration (migration vs non-migration).
 * This endpoint supports partial `w2Payroll` updates and is used to let users correct the
 * migration choice after starting onboarding.
 */
export const updateCompanyW2PayrollOnboardingConfig = async (
  companyId: string,
  data: { w2Payroll: { migrationRequired: boolean; numberOfEmployeesToMigrate?: number } },
) => {
  return await callApi(`/company/${companyId}`, 'patch', data);
};

export const getCompanyBankAccountDetails = async (companyId: string) => {
  return await callApi(`/companies/${companyId}/bank/account`, 'get', {});
};

export const updateMFAUserDetails = async (employeeID: string, data: { mfaUserDetails: { mfaType?: string } }) => {
  return await callApi(`/companies/admins/${employeeID}`, 'patch', data);
};

export const resetMFAUser = async (email: string) => {
  return await callApi(`/user/reset-mfa?email=${email}`, 'post', {});
};

export const uploadCompanyLogo = async (companyId: string, file: FormData) => {
  return await callApi(`/companies/${companyId}/upload-logo`, 'put', file);
};

export const downloadCompanyLogo = async (companyId: string) => {
  return await callApi(`/companies/${companyId}/download-logo`, 'get', {});
};

export const updateUserProfile = async (userID: string, data: { firstName: string; lastName: string }) => {
  return await callApi(`/companies/admins/${userID}`, 'patch', data);
};

export const getContractorPayoutPreview = async (
  companyId: string,
  workerUserId: string,
  data: { currency: string; amount: number; withdrawalMethodId: string },
) => {
  return await callApi(
    `/companies/${companyId}/workers/${workerUserId}/payout-preview`,
    'POST',
    data,
    undefined,
    undefined,
    true,
  );
};

// temporary before sdk
enum FeeType {
  FIXED = 'FIXED',
  VARIABLE = 'VARIABLE',
}

interface WithdrawalFee {
  /**
   * A number amount, either a percentage or currency amount.
   */
  amount: number;
  feeType: FeeType;
  /**
   * A short description of the fee.
   */
  label: string;
}

/**
 * A user-facing quote which includes fees and an exchange rate.
 */
interface WorkerWithdrawalQuote {
  /**
   * A list of fees presented to the users.
   */
  fees: WithdrawalFee[];
  /**
   * The exchange rate presented to the the user.
   */
  exchangeRate: number;
  /**
   * The currency the user wants to withdraw to.
   */
  targetCurrency: string;
  /**
   * The amount the user will see in their bank account, in the target currency.
   */
  targetAmount: number;
  /**
   * The currency the user wants to withdraw from.
   */
  sourceCurrency: string;
  /**
   * The amount (in sourceCurrency) that will be debited from the workers wallet.
   */
  sourceAmount: number;
  /**
   * Provider id
   */
  provider: string;
}
// end temporary

/**
 * Request a quote given a certain amount, currency, and withdrawal method.
 */
export const getContractorPayoutQuote = async (
  companyId: string,
  workerUserId: string,
  data: { amount: number; currency: string; withdrawalMethodId: string; includeVariableFee?: boolean },
): Promise<WorkerWithdrawalPreviewResponse> => {
  const params = `amount=${data.amount}&amountCurrency=${data.currency}&withdrawalMethodId=${data.withdrawalMethodId}`;
  const res: WorkerWithdrawalQuote = await callApi(
    `/companies/${companyId}/workers/${workerUserId}/quote?${params}`,
    'GET',
    undefined,
    undefined,
    undefined,
    true,
  );
  // The only fees we ever show right now are SWIFT (FIXED) and, when explicitly requested
  // (e.g. AstroPay), VARIABLE fees. Everything else has to be absorbed by fx server side.
  const swiftFees = res.fees.filter((fee) => fee.feeType === FeeType.FIXED);
  const swiftFee = swiftFees.reduce((acc, curr) => acc + curr.amount, 0);
  const variableFee = data.includeVariableFee
    ? res.fees.filter((fee) => fee.feeType === FeeType.VARIABLE).reduce((acc, curr) => acc + curr.amount, 0)
    : 0;
  const quote: WorkerWithdrawalPreviewResponse = {
    feeCurrency: res.sourceCurrency,
    flatFee: swiftFee,
    variableFee,
    exchangeRate: res.exchangeRate,
    exchangeFrom: res.sourceAmount - swiftFee - variableFee,
    exchangeTo: res.targetAmount,
  };
  return quote;
};

/**
 * Request a withdrawal given certain debit/credit amounts.
 * Debit amount is what is removed from the worker's (ledger) balance.
 * Credit amount is what to ask for from payout provider.
 */
export const requestWithdrawal = async (
  workerUserId: string,
  companyId: string,
  data: InitiateWorkerWithdrawalRequest,
): Promise<{ withdrawalId: string }> => {
  return await callApi(
    `/companies/${companyId}/workers/${workerUserId}/withdraw`,
    'POST',
    data,
    undefined,
    undefined,
    true,
  );
};
